/**
 * Color Mode Provider
 *
 * Manages light/dark color mode preference.
 * Applies/removes `.dark` class on <html> to activate CSS variable overrides.
 * Persists choice to localStorage under `tf-theme`.
 *
 * Separate from the Emotion ThemeProvider — this controls CSS custom properties,
 * not the Emotion theme object.
 */

'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

type ColorMode = 'light' | 'dark';

interface ColorModeContextValue {
  colorMode: ColorMode;
  resolvedMode: 'light' | 'dark';
  setColorMode: (mode: ColorMode) => void;
}

const STORAGE_KEY = 'tf-theme';

function getStoredMode(): ColorMode {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
}

function applyMode(resolved: 'light' | 'dark'): void {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

const ColorModeContext = createContext<ColorModeContextValue>({
  colorMode: 'light',
  resolvedMode: 'light',
  setColorMode: () => {},
});

export interface ColorModeProviderProps {
  children: ReactNode;
  defaultMode?: ColorMode;
}

export function ColorModeProvider({ children, defaultMode }: ColorModeProviderProps) {
  const [colorMode, setColorModeState] = useState<ColorMode>(() => defaultMode ?? getStoredMode());

  const resolvedMode: 'light' | 'dark' = colorMode;

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, []);

  // Apply .dark class whenever resolved mode changes
  useEffect(() => {
    applyMode(resolvedMode);
  }, [resolvedMode]);

  return (
    <ColorModeContext.Provider value={{ colorMode, resolvedMode, setColorMode }}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext);
}
